import axios from 'axios';
import { SearchResultDto } from '../../model/dto/searchResultDto';
import { LandingSearchResponseDto } from '../../model/dto/landingSearchResponseDto';
import { ArticleSearchResponseDto } from '../../model/dto/articleSearchResponseDto';
import { ArticleSearchWithFiltersResponseDto } from '../../model/dto/articleSearchWithFiltersResponseDto';
import { ProductSearchResponseDto } from '../../model/dto/productSearchResponseDto';
import { AssetsListResponseDto } from '../../model/dto/assetsListResponseDto';
import { AssetSearchParams } from '../../model/dto/assetSearchParams';
import AuthApi from './AuthApi';


class SearchApi {
  Search = async (
    searchTerm: string | null,
    header: string | null | undefined, 
    h1:string | null | undefined, 
    h2:string | null | undefined, 
    h3:string | null | undefined, 
    h4:string | null | undefined, 
    h5:string | null | undefined, 
    h6:string | null | undefined, 
    src:string | null | undefined, 
    product:string | null | undefined, 
    locale:string | null | undefined, 
    source:string | null | undefined, 
    signal?: AbortSignal
    ): Promise<SearchResultDto[]> => {

    if(!!searchTerm){
      //@ts-ignore
      posthog.capture('Query searched', {query: searchTerm, product: product, source: source })
    }

    let url = `${process.env.REACT_APP_API_URL}/search`;

    
    const searchParams = new URLSearchParams();
    if(searchTerm) searchParams.append("query", searchTerm);
    if(header) searchParams.append("header", header);
    if(h1) searchParams.append("h1", h1);
    if(h2) searchParams.append("h2", h2);
    if(h3) searchParams.append("h3", h3);
    if(h4) searchParams.append("h4", h4);
    if(h5) searchParams.append("h5", h5);
    if(h6) searchParams.append("h6", h6);
    if(src) searchParams.append("src", src);
    if(product) searchParams.append("productLines", product);
    if(locale) searchParams.append("locale", locale);
    if(source) searchParams.append("source", source);
    
    url = `${url}?${searchParams.toString()}`;

    return axios
      .get(url, { signal : signal })
      .then((response) => {
        if (response.status === 200) {
          return response.data;
        }
      });
  };

  MainSearch = async (
    searchTerm: string,
    signal?: AbortSignal
  ): Promise<LandingSearchResponseDto> => {
    let url = `${process.env.REACT_APP_API_URL}/search/main`;
    
    if (searchTerm) {
      url = `${url}?query=${encodeURIComponent(searchTerm)}`;
    }

    const headers: Record<string, string> = {};
    try {
      const token = await AuthApi.getToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    } catch {
      // User not authenticated — continue without token
    }
    
    return axios.get(url, { signal, headers }).then((response) => {
      if (response.status === 200) {
        return response.data;
      }
    });
  };

  ArticleSearch = async (
    searchTerm: string | null,
    pageNumber: number = 0,
    itemsPerPage: number = 20,
    lang?: string,
    signal?: AbortSignal
  ): Promise<ArticleSearchResponseDto> => {
    const searchParams = new URLSearchParams();
    if (searchTerm) searchParams.append("query", searchTerm);
    searchParams.append("pageNumber", pageNumber.toString());
    searchParams.append("itemsPerPage", itemsPerPage.toString());
    if (lang) searchParams.append("lang", lang);

    const url = `${process.env.REACT_APP_API_URL}/search/articles?${searchParams.toString()}`;

    return axios.get(url, { signal }).then((response) => {
      if (response.status === 200) {
        return response.data;
      }
    });
  };

  ArticleSearchWithFilters = async (
    searchTerm: string | null,
    pageNumber: number = 0,
    itemsPerPage: number = 20,
    productLineCodes?: string[],
    articleFilterSlugs?: string[],
    lang?: string,
    signal?: AbortSignal
  ): Promise<ArticleSearchWithFiltersResponseDto> => {
    const searchParams = new URLSearchParams();
    if (searchTerm) searchParams.append("query", searchTerm);
    searchParams.append("pageNumber", pageNumber.toString());
    searchParams.append("itemsPerPage", itemsPerPage.toString());
    if (productLineCodes) {
      productLineCodes.forEach(code => searchParams.append("productLineCodes", code));
    }
    if (articleFilterSlugs) {
      articleFilterSlugs.forEach(slug => searchParams.append("articleFilterSlugs", slug));
    }
    if (lang) searchParams.append("lang", lang);

    const url = `${process.env.REACT_APP_API_URL}/search/articles/withfilters?${searchParams.toString()}`;

    return axios.get(url, { signal }).then((response) => {
      if (response.status === 200) {
        return response.data;
      }
    });
  };

  ProductSearch = async (
    searchTerm: string | null,
    pageNumber: number = 0,
    itemsPerPage: number = 20,
    lang?: string,
    signal?: AbortSignal
  ): Promise<ProductSearchResponseDto> => {
    const searchParams = new URLSearchParams();
    if (searchTerm) searchParams.append("query", searchTerm);
    searchParams.append("pageNumber", pageNumber.toString());
    searchParams.append("itemsPerPage", itemsPerPage.toString());
    if (lang) searchParams.append("lang", lang);

    const url = `${process.env.REACT_APP_API_URL}/search/products?${searchParams.toString()}`;

    return axios.get(url, { signal }).then((response) => {
      if (response.status === 200) {
        return response.data;
      }
    });
  };

  /**
   * Searches assets (products) using the Connect API via our backend.
   * Requires the user to be authenticated.
   */
  AssetSearch = async (params?: AssetSearchParams): Promise<AssetsListResponseDto> => {
    const queryParams = new URLSearchParams();

    if (params?.search) queryParams.append('search', params.search);
    if (params?.filter) queryParams.append('filter', params.filter);
    if (params?.sort) queryParams.append('sort', params.sort);
    if (params?.hidden) queryParams.append('hidden', 'true');
    if (params?.page !== undefined && params?.page !== null) queryParams.append('page', params.page.toString());
    if (params?.pageSize !== undefined && params?.pageSize !== null) queryParams.append('pageSize', params.pageSize.toString());
    if (params?.productLineName) queryParams.append('productLineName', params.productLineName);

    const queryString = queryParams.toString();
    const url = `${process.env.REACT_APP_API_URL}/search/asset${queryString ? `?${queryString}` : ''}`;

    return axios
      .get<AssetsListResponseDto>(url, {
        headers: {
          Authorization: `Bearer ${await AuthApi.getToken()}`,
        },
      })
      .then((response) => {
        if (response.status === 200) {
          return response.data;
        }
        throw new Error(`Asset search failed with status ${response.status}`);
      });
  };
  
}

export default new SearchApi;